import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { prescriptionAPI } from "@/lib/prescription-api";
import {
  CreateFollowUpRequest,
  UpdateFollowUpRequest,
} from "@/types/prescription";

export const useFollowup = (
  appointmentId: string,
  parentType: "prescription" | "meal_plan" = "prescription"
) => {
  const queryClient = useQueryClient();
  // Include parentType in queryKey to separate cache for doctor vs nutritionist
  const queryKey = ["followup", appointmentId, parentType];

  const query = useQuery({
    queryKey,
    queryFn: () => prescriptionAPI.getFollowUp(appointmentId, parentType),
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateFollowUpRequest) =>
      prescriptionAPI.createFollowUp(appointmentId, data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFollowUpRequest }) =>
      prescriptionAPI.updateFollowUp(appointmentId, id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
};
